import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { currentUserQueryKey, fetchCurrentUser } from '@/hooks/currentUserQuery';
import { type PlatformUser, updateMe } from '@/services/users';

export interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  avatar_color: string | null;
  /** When false, the finance full tour prompt is shown (user_finance_tour). Set true when user completes or dismisses the tour. */
  finance_tour_completed: boolean | null;
}

interface UserProfileContextValue {
  profile: UserProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  setFinanceTourCompleted: (completed: boolean) => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

function toUserProfile(me: PlatformUser): UserProfile {
  return {
    first_name: me.first_name ?? null,
    last_name: me.last_name ?? null,
    avatar_url: me.avatar_url ?? null,
    avatar_color: me.avatar_color ?? null,
    finance_tour_completed: me.finance_tour_completed ?? false,
  };
}

export function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: me, isLoading } = useQuery({
    queryKey: currentUserQueryKey(user?.id),
    queryFn: fetchCurrentUser,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const profile = useMemo(() => (me ? toUserProfile(me) : null), [me]);

  const updateProfile = useCallback(
    async (updates: Partial<UserProfile>): Promise<void> => {
      if (!user) throw new Error('Not authenticated');
      setIsSaving(true);
      try {
        const saved = await updateMe(updates);
        queryClient.setQueryData(currentUserQueryKey(user.id), saved);
      } finally {
        setIsSaving(false);
      }
    },
    [user, queryClient]
  );

  const setFinanceTourCompleted = useCallback(
    async (completed: boolean): Promise<void> => {
      await updateProfile({ finance_tour_completed: completed });
    },
    [updateProfile]
  );

  return (
    <UserProfileContext.Provider value={{ profile, isLoading, isSaving, updateProfile, setFinanceTourCompleted }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) throw new Error('useUserProfile must be used within a UserProfileProvider');
  return context;
}
